import { logAmplitudeEvent } from 'amplitude/amplitude'
import { AdPlacementCode, LEADERBOARD_FORMATS } from 'elements/ads/AdPlacements'
import BannerAd from 'elements/BannerAd'
import { AVAILABILITY_FILTER_KEY } from 'elements/hardwareFilter/stockAvailability/AvailabilityFilterNodeFactory'
import { memo, useEffect } from 'react'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import type { ComponentTypes, ComponentTypesV2 } from 'types/selectComponent'
import QueryControlledCatalog from './catalog/QueryControlledCatalog'
import ShopItemsWithFilter from './catalogv3/ShopItemsWithFilter'

import { orgSelectors } from 'ducks/orgs'
import { DistributorTradePricingContext } from 'elements/hardwareSelectorV2/HardwareSelectorCore'
import { ComponentVersionsInherit, ComponentVersions_3_0 } from 'opensolar-ui'
import { useSelector } from 'react-redux'
import useDistributorTradePricing from 'services/useDistributorTradePricing'
import { useFeatureFlag } from '../../util/featureFlags'
import MarketplaceDisclaimerBanner from './elements/MarketplaceDisclaimerBanner'
import ShopPageContainer from './elements/ShopPageContainer'
import useEnabledHardwareSuppliers from './hooks/useEnabledHardwareSuppliers'

const useStyles = makeOpenSolarStyles((theme) => ({
  container: { margin: '20px 0' },
  containerV3: { background: '#F6F9FC' },
}))

const getShopResultsPageAdPlacement = ({
  searchText,
  componentType,
}: {
  searchText: string
  componentType?: ComponentTypes
}): AdPlacementCode | undefined => {
  if (componentType) {
    return `shop_category_${componentType}_bottom` as AdPlacementCode
  }
  return undefined
}

const TRACKING_SOURCE = 'hardware_search_catalog_view'

/*
    Page url formats:
      /shop/search/{searchText}
      or
      /shop/search/{componentType}

    Filter query params example:
      /shop/search/module/?filter={filter key value pairs}

    Note: Filter query params change will not trigger a catalog remount, but if the searchText and componentType are changed a remount is expected.

    @searchText:
        search text for filtering
    @componentType:
        component type used for filtering purpose
*/

const ShopCatalog = ({
  searchText,
  componentType,
  initialComponentsData,
  initialTitle,
  otherSubtypes,
  categoryName,
  distributor,
}: {
  searchText: string
  componentType?: ComponentTypes
  initialComponentsData?: any[]
  initialTitle?: string
  otherSubtypes?: string[]
  categoryName?: string
  distributor?: string
}) => {
  const enabledHardwareShopV3 = useFeatureFlag('enable_multi_distributor_shop', 'on')

  if (enabledHardwareShopV3) {
    return (
      <ShopItemsWithFilter
        searchText={searchText}
        trackSource={TRACKING_SOURCE}
        componentType={componentType as ComponentTypesV2}
        initialComponentsData={initialComponentsData}
        initialTitle={initialTitle}
        otherSubtypes={otherSubtypes}
        categoryName={categoryName}
        distributor={distributor as any}
      />
    )
  }

  const initialFilterValues = {
    q: searchText,
    [AVAILABILITY_FILTER_KEY]: 'true',
  }

  const title = searchText && <h1>{`Search Results: ${searchText}`}</h1>
  return (
    <QueryControlledCatalog
      trackSource={TRACKING_SOURCE}
      initialFilterValues={initialFilterValues}
      title={title}
      componentType={componentType}
    />
  )
}

const ShopResultsPage = ({
  searchText,
  componentType,
  initialComponentsData,
  initialTitle,
  otherSubtypes,
  categoryName,
  distributor,
}: {
  searchText: string
  componentType?: ComponentTypes
  initialComponentsData?: any[]
  initialTitle?: string
  otherSubtypes?: string[]
  categoryName?: string
  distributor?: string
}) => {
  const classes = useStyles()
  const enabledHardwareShopV3 = useFeatureFlag('enable_multi_distributor_shop', 'on')

  useEffect(() => {
    logAmplitudeEvent('page_viewed', {
      page: TRACKING_SOURCE,
      searchText,
      componentType,
    })
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [searchText, componentType, categoryName, distributor])

  const { filteredSuppliers: hardwareDistributors } = useEnabledHardwareSuppliers()

  const { distributorTradePricing, isChecking } = useDistributorTradePricing({
    orgId: useSelector(orgSelectors.getOrg)?.id || 0,
    distributors: hardwareDistributors,
  })

  const adPlacementBottom = getShopResultsPageAdPlacement({ searchText, componentType })

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <DistributorTradePricingContext.Provider value={{ distributorTradePricing, isChecking }}>
        <div className={enabledHardwareShopV3 ? classes.containerV3 : classes.container}>
          <ShopCatalog
            searchText={searchText}
            componentType={componentType}
            initialComponentsData={initialComponentsData}
            initialTitle={initialTitle}
            otherSubtypes={otherSubtypes}
            categoryName={categoryName}
            distributor={distributor}
          />
          {!!adPlacementBottom && (
            <BannerAd
              formats={LEADERBOARD_FORMATS}
              placement={adPlacementBottom}
              containerStyle={{ marginTop: 10, marginBottom: 20 }}
            />
          )}
          <ShopPageContainer surfaceColor={null} disableVerticalPadding>
            <MarketplaceDisclaimerBanner scrollIntoViewOnExpand />
          </ShopPageContainer>
        </div>
      </DistributorTradePricingContext.Provider>
    </ComponentVersionsInherit>
  )
}

export default memo(ShopResultsPage)
